/**
 * World Persistence Service — per-object write-through.
 *
 * Every broadcast object mutation is also written to world_assets, keyed by
 * (world_id, client_id), so builders' changes survive without waiting for the
 * owner's manual save and no one's save clobbers anyone else's work.
 *
 * Guards:
 * - Only for real DB worlds (UUID ids) — temp worlds still live in memory.
 * - Only when this client canBuild (viewers never write).
 * - Only when the loaded world's rows carry stable ids (persistenceReady) —
 *   legacy worlds keep the manual-save path until the owner saves once,
 *   which stamps ids on every row.
 *
 * The serialization here must mirror WorldBuilder.saveWorld so manual saves
 * and write-through produce identical rows.
 */

import { supabase } from '../config/supabase';
import { useWorldStore } from '../stores/worldStore';
import { toOriginalUrls } from '../stores/liteStore';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const UPDATE_DEBOUNCE_MS = 800;

const pendingUpdates = new Map<string, ReturnType<typeof setTimeout>>();
let warnedOnce = false;

function warn(error: { message?: string }) {
  if (warnedOnce) return;
  warnedOnce = true;
  console.warn(
    '[Persistence] Per-object write failed — has the per_object_assets migration been applied?',
    error?.message || error
  );
}

function canPersist(worldId: string): boolean {
  if (!UUID_RE.test(worldId)) return false;
  const s = useWorldStore.getState();
  return !!s.user && s.canBuild && s.persistenceReady;
}

function itemFromStore(objectType: string, id: string): any | null {
  const s = useWorldStore.getState();
  if (objectType === 'primitive') return s.primitives[id] || null;
  if (objectType === 'light') return s.lights[id] || null;
  if (objectType === 'content') return s.contentItems[id] || null;
  if (objectType === 'environment') return s.environmentModels[id] || null;
  if (objectType === 'skybox') return s.skyboxes[id] || null;
  if (objectType === 'screenprop') return s.screenProps[id] || null;
  return null;
}

function serializeRow(worldId: string, objectType: string, id: string, item: any) {
  if (objectType === 'primitive') {
    return {
      world_id: worldId, client_id: id, type: 'primitive', url: '',
      metadata: {
        id, shape: item.shape, name: item.name, position: item.position, rotation: item.rotation,
        scale: item.scale, color: item.color, texturePreset: item.texturePreset, textureUrl: item.textureUrl,
        pbrMaps: item.pbrMaps, textureScale: item.textureScale, textureScaleY: item.textureScaleY, visible: item.visible, isLocked: item.isLocked,
        collidable: item.collidable, colliderOnly: item.colliderOnly, opacity: item.opacity,
        metalness: item.metalness, roughness: item.roughness, emissive: item.emissive ?? 0, seat: item.seat ?? false, ...(item.seatFit ? { seatFit: item.seatFit } : {}), portalLink: item.portalLink, portalLabel: item.portalLabel, portalWorldId: item.portalWorldId, portalWorldName: item.portalWorldName, game: item.game, gameStyle: item.gameStyle, ...(item.arcadeGame ? { arcadeGame: item.arcadeGame } : {}), ...(item.board ? { board: item.board } : {}), ...(item.speaker ? { speaker: item.speaker } : {}), ...(item.worldGame ? { worldGame: item.worldGame } : {}), npc: item.npc, groupId: item.groupId, text: item.text, fontSize: item.fontSize,
        ...(item.castShadow === false ? { castShadow: false } : {}),
      },
    };
  }
  if (objectType === 'light') {
    return {
      world_id: worldId, client_id: id, type: 'light', url: '',
      metadata: {
        id, lightType: item.type, name: item.name, position: item.position, rotation: item.rotation,
        color: item.color, intensity: item.intensity, distance: item.distance, angle: item.angle,
        penumbra: item.penumbra, castShadow: item.castShadow, visible: item.visible, isLocked: item.isLocked,
        width: item.width, height: item.height,
      },
    };
  }
  if (objectType === 'content') {
    return {
      world_id: worldId, client_id: id, type: 'content', url: item.url,
      metadata: {
        id, position: item.position, scale: item.scale, rotation: item.rotation, visible: item.visible, isLocked: item.isLocked || false,
        filename: item.filename, thumbnailUrl: item.thumbnailUrl, uploadedAt: item.uploadedAt,
        contentType: item.type, collidable: item.collidable || false, seat: item.seat || false, environmentMesh: item.environmentMesh || false, mobileUrl: item.mobileUrl, materialColors: item.materialColors, materialTextures: item.materialTextures, groupId: item.groupId,
      },
    };
  }
  if (objectType === 'environment') {
    return {
      world_id: worldId, client_id: id, type: 'environment', url: item.url,
      metadata: {
        id, position: item.transform.position, scale: item.transform.scale,
        rotation: item.transform.rotation, visible: item.visible, isLocked: item.transform.isLocked,
        ...(item.collision === 'none' ? { collision: 'none' } : {}),
      },
    };
  }
  if (objectType === 'skybox') {
    return {
      world_id: worldId, client_id: id, type: 'skybox', url: item.url,
      metadata: {
        id, filename: item.filename, uploadedAt: item.uploadedAt, thumbnail: item.thumbnail,
        isCurrent: id === useWorldStore.getState().currentSkyboxId,
      },
    };
  }
  if (objectType === 'screenprop') {
    // Persist the panel and its placement, never the live MediaStream.
    return {
      world_id: worldId, client_id: id, type: 'screenprop', url: item.browserUrl || item.videoUrl || '',
      metadata: {
        id, screenType: item.type, position: item.position, scale: item.scale,
        rotation: item.rotation, visible: item.visible, isLocked: item.isLocked ?? false,
        browserUrl: item.browserUrl, videoUrl: item.videoUrl,
        filename: item.filename, uploadedAt: item.uploadedAt,
        ...(item.roomScreen ? { roomScreen: true, width: item.width, height: item.height, name: item.name } : {}),
      },
    };
  }
  return null;
}

async function upsertRow(row: NonNullable<ReturnType<typeof serializeRow>>) {
  // Never persist a lite-rendition URL (headset/phone) in place of the original.
  const { error } = await supabase
    .from('world_assets')
    .upsert({ ...toOriginalUrls(row), updated_at: new Date().toISOString() } as any, { onConflict: 'world_id,client_id' });
  if (error) warn(error);
}

export function persistObjectAdd(worldId: string, objectType: string, id: string, item: any) {
  if (!canPersist(worldId)) return;
  const row = serializeRow(worldId, objectType, id, item);
  if (row) void upsertRow(row);
}

export function persistObjectUpdate(worldId: string, objectType: string, id: string) {
  if (!canPersist(worldId)) return;
  const key = `${worldId}:${id}`;
  const pending = pendingUpdates.get(key);
  if (pending) clearTimeout(pending);
  pendingUpdates.set(key, setTimeout(() => {
    pendingUpdates.delete(key);
    const item = itemFromStore(objectType, id);
    if (!item) return; // deleted while debouncing
    const row = serializeRow(worldId, objectType, id, item);
    if (row) void upsertRow(row);
  }, UPDATE_DEBOUNCE_MS));
}

export function persistObjectDelete(worldId: string, objectType: string, id: string) {
  if (!canPersist(worldId)) return;
  const key = `${worldId}:${id}`;
  const pending = pendingUpdates.get(key);
  if (pending) { clearTimeout(pending); pendingUpdates.delete(key); }
  supabase
    .from('world_assets')
    .delete()
    .eq('world_id', worldId)
    .eq('client_id', id)
    .then(({ error }) => { if (error) warn(error); });
}
